import React, { useEffect, useState, useRef } from 'react';
import { Container } from './styles';
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridTreeNodeWithRender,
  GridColumnVisibilityModel,
  GridToolbar,
} from '@mui/x-data-grid';
import {
  Grid,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Popper,
  Paper,
  ClickAwayListener,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import infoIcon from '../../../../assets/images/info.svg';
import editIcon from '../../../../assets/images/editIcon.svg';
import editIconHover from '../../../../assets/images/editIconHover.svg';
import xGrayIcon from '../../../../assets/images/xgray_Icon.svg';
import checkGrayIcon from '../../../../assets/images/checkgray_Icon.svg';
import trashIcon from '../../../../assets/images/trashIcon.svg';
import EditSolicitationModal from '../EditSolicitationModal';
import { BusinessStatusAPI, IBusiness, SegmentationAPI } from 'interfaces/IBusinesses';
import IEvent from 'interfaces/IEvent';
import touristOffersBusinessService from 'services/TouristOffersBusinessService';
import { useLoading } from 'hooks/useLoading';
import DeleteSolicitationModal from '../DeleteSolicitationModal';
import ApproveSolicitationModal from '../ApproveSolicitationModal';
import { useHistory } from 'react-router-dom';
import RemoveSolicitationModal from '../RemoveSolicitationModal';
import UserService from 'services/UserService';

const BusinessList = (): JSX.Element => {
  const { openLoading, closeLoading } = useLoading();
  const [businessData, setBusinessData] = useState<IBusiness[]>([]);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({
    id: false,
  });
  const [openModalEditSolicitationData, setOpenModalEditSolicitationData] = useState(false);
  const [openModalDeleteSolicitationData, setOpenModalDeleteSolicitationData] = useState(false);
  const [openModalApproveSolicitationData, setOpenModalApproveSolicitationData] = useState(false);
  const [openModalRemoveSolicitationData, setOpenModalRemoveSolicitationData] = useState(false);
  const [showAdminSettings, setShowAdminSettings] = useState(false);
  const [openModalMoreInfo, setOpenModalMoreInfo] = useState({} as IBusiness | IEvent);
  const [openModalDeleteInfo, setOpenModalDeleteInfo] = useState({} as IBusiness | IEvent);
  const [openModalApproveInfo, setOpenModalApproveInfo] = useState({} as IBusiness | IEvent);
  const [openModalRemoveInfo, setOpenModalRemoveInfo] = useState({} as IBusiness | IEvent);
  const [selectedSegments, setSelectedSegments] = useState<string[]>([]);
  const [openSegmentDropdown, setOpenSegmentDropdown] = useState(false);
  const segmentAnchorRef = useRef<HTMLDivElement>(null);

  const history = useHistory();

  const segmentOptions = [
    { label: 'Hospedagem', value: SegmentationAPI.Acommodations },
    { label: 'Restaurantes', value: SegmentationAPI.Restaurants },
    { label: 'Transportes', value: SegmentationAPI.Transports },
    { label: 'Passeio e Lazer', value: SegmentationAPI.Tours },
  ];

  useEffect(() => {
    openLoading();
    const fetchBusinessData = [fetchTouristOffersBusiness()];
    Promise.all(fetchBusinessData).finally(() => closeLoading());
  }, []);

  useEffect(() => {
    const fetchUser = async () => {
      openLoading();
      try {
        openLoading();
        const user = await new UserService().findOne();
        if (user) {
          setShowAdminSettings(user.type === 'admin');
        }
      } catch (error) {
        console.error(error);
      } finally {
        closeLoading();
      }
    };

    fetchUser();
  }, []);

  const service = {
    touristOffersBusiness: new touristOffersBusinessService(),
  };

  const fetchTouristOffersBusiness = async () => {
    const data = await service.touristOffersBusiness.findAllTrades();
    setBusinessData(data);
  };

  const handleSegmentChange = (value: string) => {
    const newSelected = selectedSegments.includes(value)
      ? selectedSegments.filter(s => s !== value)
      : [...selectedSegments, value];
    setSelectedSegments(newSelected);
  };

  const filteredRows = businessData.filter(business => {
    if (selectedSegments.length === 0) return true;
    return selectedSegments.includes(business.type as string);
  });

  const stylingRowStatus = (status?: BusinessStatusAPI) => {
    switch (status) {
      case BusinessStatusAPI.APPROVED:
        return <span className="approvedBadge">Aprovado</span>;
      case BusinessStatusAPI.REFUSED:
        return <span className="reprovedBadge">Recusado</span>;
      case BusinessStatusAPI.WAITING_FOR_SETUR:
        return <span className="waitingSeturBadge">Aguardando SETUR</span>;
      case BusinessStatusAPI.WAITING_FOR_TRADE:
        return <span className="waitingTradeBadge">Aguardando Trade</span>;
      default:
        return '- - -';
    }
  };

  const stylingSegmentation = (segmentation?: SegmentationAPI) => {
    switch (segmentation) {
      case SegmentationAPI.Acommodations:
        return 'Hospedagem';
      case SegmentationAPI.Restaurants:
        return 'Restaurantes (Alimentos e Bebidas)';
      case SegmentationAPI.Transports:
        return 'Transportes';
      case SegmentationAPI.Tours:
        return 'Passeio e Lazer';
      default:
        return '- - -';
    }
  };

  const handleBusinessDisabledButton = (id: string) => {
    const business = businessData.find(business => business.id === Number(id));
    if (business?.status !== BusinessStatusAPI.WAITING_FOR_SETUR) {
      return true;
    }
    return false;
  };

  const handleBusinessEditDisabledButton = (id: string) => {
    const business = businessData.find(business => business.id === Number(id));
    if (business?.status !== BusinessStatusAPI.WAITING_FOR_SETUR && business?.status !== BusinessStatusAPI.APPROVED) {
      return true;
    }
    return false;
  };

  const businessEditSolicitation = (id: string) => () => {
    openLoading();
    const businessItem = businessData.find(business => business.id === Number(id));
    if (businessItem) {
      setOpenModalMoreInfo(businessItem);
    }
    setOpenModalEditSolicitationData(true);
    closeLoading();
  };

  const businessDeleteSolicitation = (id: string) => () => {
    openLoading();
    const businessItem = businessData.find(business => business.id === Number(id));
    if (businessItem) {
      setOpenModalDeleteInfo(businessItem);
    }
    setOpenModalDeleteSolicitationData(true);
    closeLoading();
  };

  const businessApproveSolicitation = (id: string) => () => {
    openLoading();
    const businessItem = businessData.find(business => business.id === Number(id));
    if (businessItem) {
      setOpenModalApproveInfo(businessItem);
    }
    setOpenModalApproveSolicitationData(true);
    closeLoading();
  };

  const businessRemove = (id: string) => async () => {
    openLoading();
    const businessItem = businessData.find(business => business.id === Number(id));
    if (businessItem) {
      setOpenModalRemoveInfo(businessItem);
    }
    setOpenModalRemoveSolicitationData(true);
    closeLoading();
  };

  const formatCreatedAtDate = (createdAt: string): JSX.Element => {
    const date = new Date(createdAt);
    const formattedDate = date.toLocaleDateString('pt-BR');
    const formattedTime = date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

    return (
      <div className="createdDate">
        <span>{formattedDate}</span>
        <span>{formattedTime}</span>
      </div>
    );
  };

  const businessColumns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'ID',
      flex: 1,
      minWidth: 50,
      align: 'center',
      headerAlign: 'center',
      resizable: true,
    },
    {
      field: 'createdAt',
      headerName: 'Criação',
      flex: 1,
      minWidth: 200,
      align: 'center',
      headerAlign: 'center',
      resizable: true,
      renderCell: (params: GridRenderCellParams<IBusiness, string, keyof IBusiness, GridTreeNodeWithRender>) =>
        formatCreatedAtDate(params.value as string),
    },
    {
      field: 'businessOwner',
      headerName: 'Nome do Trade',
      flex: 1,
      minWidth: 220,
      align: 'center',
      headerAlign: 'center',
      resizable: true,
    },
    {
      field: 'tradeName',
      headerName: 'Nome do Negócio',
      flex: 1,
      minWidth: 220,
      align: 'center',
      headerAlign: 'center',
      resizable: true,
    },
    {
      field: 'title',
      headerName: 'Título da Atividade',
      flex: 1,
      minWidth: 220,
      align: 'center',
      headerAlign: 'center',
      resizable: true,
    },
    {
      field: 'type',
      headerName: 'Segmento',
      flex: 1,
      minWidth: 220,
      renderCell: (params: GridRenderCellParams<IBusiness, SegmentationAPI, keyof IBusiness, GridTreeNodeWithRender>) =>
        stylingSegmentation(params.value),
      align: 'center',
      headerAlign: 'center',
      resizable: true,
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      minWidth: 222,
      renderCell: (
        params: GridRenderCellParams<IBusiness, BusinessStatusAPI, keyof IBusiness, GridTreeNodeWithRender>
      ) => stylingRowStatus(params.value),
      align: 'center',
      headerAlign: 'center',
      resizable: true,
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Ações',
      flex: 1,
      minWidth: 285,
      getActions: params => [
        <div key={params.id}>
          <button
            className="button-icon"
            title="Mais informações"
            onClick={() => history.push(`/admin/business-list-overview/id=${params.id}`)}>
            <img src={infoIcon} alt="Mais informações" />
          </button>
          {showAdminSettings && (
            <button
              className="button-icon"
              title="Editar"
              onClick={() => {
                history.push({
                  pathname: `/admin/trade/my-business-edit/${params.id}`,
                  state: { from: '/admin/tourist-offers/business-list' },
                });
              }}>
              <img src={editIconHover} alt="Editar" />
            </button>
          )}
          <button
            className="button-icon"
            title="Soliciar Edição"
            onClick={businessEditSolicitation(params.id.toString())}
            disabled={handleBusinessEditDisabledButton(params.id.toString())}>
            <img src={editIcon} alt="Editar" />
          </button>
          <button
            className="button-icon"
            title="Recusar"
            onClick={businessDeleteSolicitation(params.id.toString())}
            disabled={handleBusinessDisabledButton(params.id.toString())}>
            <img src={xGrayIcon} alt="Recusar" />
          </button>
          <button
            className="button-icon"
            title="Confirmar"
            onClick={businessApproveSolicitation(params.id.toString())}
            disabled={handleBusinessDisabledButton(params.id.toString())}>
            <img src={checkGrayIcon} alt="Confirmar" />
          </button>
          <button className="button-icon" title="Remover" onClick={businessRemove(params.id.toString())}>
            <img src={trashIcon} alt="Remover" />
          </button>
        </div>,
      ],
      align: 'center',
      headerAlign: 'center',
      resizable: true,
    },
  ];

  return (
    <Container>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%', mb: 2 }}>
        <Box ref={segmentAnchorRef} sx={{ position: 'relative', width: '300px' }}>
          <FormControl fullWidth>
            {selectedSegments.length === 0 && (
              <InputLabel
                shrink={false}
                sx={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#9A9A9A',
                  pointerEvents: 'none',
                  fontFamily: 'Signika',
                  zIndex: 1,
                }}>
                Filtrar por Segmento
              </InputLabel>
            )}

            <Select
              multiple
              open={false}
              onClick={() => setOpenSegmentDropdown(!openSegmentDropdown)}
              value={selectedSegments}
              className={selectedSegments.length > 0 ? 'select-filled' : ''}
              renderValue={selected => {
                const labels = (selected as string[]).map(val => segmentOptions.find(o => o.value === val)?.label);
                return labels.join(', ');
              }}
              IconComponent={props => (
                <ExpandMoreIcon {...props} sx={{ color: selectedSegments.length > 0 ? '#000' : '#9A9A9A' }} />
              )}
            />
          </FormControl>

          <Popper
            open={openSegmentDropdown}
            anchorEl={segmentAnchorRef.current}
            placement="bottom-start"
            sx={{ zIndex: 1200, width: segmentAnchorRef.current?.offsetWidth }}>
            <ClickAwayListener onClickAway={() => setOpenSegmentDropdown(false)}>
              <Paper sx={{ borderRadius: '10px', boxShadow: '0px 4px 12px rgba(0,0,0,0.1)', mt: 1 }}>
                {segmentOptions.map(option => (
                  <MenuItem
                    key={option.value}
                    onClick={() => handleSegmentChange(option.value as string)}
                    sx={{ padding: '8px 12px' }}>
                    <Checkbox
                      checked={selectedSegments.includes(option.value as string)}
                      size="small"
                      sx={{
                        color: 'rgba(204, 204, 204, 0.8)',
                        '&.Mui-checked': {
                          color: '#FFD700',
                        },
                      }}
                    />
                    <ListItemText
                      primary={option.label}
                      primaryTypographyProps={{ fontSize: '14px', fontFamily: 'Signika', color: '#666' }}
                    />
                  </MenuItem>
                ))}
              </Paper>
            </ClickAwayListener>
          </Popper>
        </Box>
      </Box>

      <Grid sx={{ width: '100%', marginBottom: '50px', textAlign: 'center' }}>
        <DataGrid
          sx={{
            background: '#fff',
            boxShadow: '0px 0px 10px 0px rgba(0,0,0,0.1)',
            borderRadius: '18px',
            width: '100%',
          }}
          rows={filteredRows}
          columns={businessColumns}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 10,
              },
            },
          }}
          pageSizeOptions={[10]}
          className="dataGrid"
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={newModel => setColumnVisibilityModel(newModel)}
          slots={{ toolbar: GridToolbar }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              densitySelector: false,
              exportButton: false,
            },
          }}
          disableRowSelectionOnClick
          disableColumnFilter
          disableColumnSelector
          disableColumnMenu
          disableDensitySelector
          disableVirtualization
        />
      </Grid>

      {openModalEditSolicitationData && (
        <EditSolicitationModal
          isOpen={openModalEditSolicitationData}
          toggle={() => setOpenModalEditSolicitationData(false)}
          item={openModalMoreInfo}
        />
      )}

      {openModalDeleteSolicitationData && (
        <DeleteSolicitationModal
          isOpen={openModalDeleteSolicitationData}
          toggle={() => setOpenModalDeleteSolicitationData(false)}
          item={openModalDeleteInfo}
        />
      )}

      {openModalApproveSolicitationData && (
        <ApproveSolicitationModal
          isOpen={openModalApproveSolicitationData}
          toggle={() => setOpenModalApproveSolicitationData(false)}
          item={openModalApproveInfo}
        />
      )}

      {openModalRemoveSolicitationData && (
        <RemoveSolicitationModal
          isOpen={openModalRemoveSolicitationData}
          toggle={() => setOpenModalRemoveSolicitationData(false)}
          item={openModalRemoveInfo}
        />
      )}
    </Container>
  );
};

export default BusinessList;
